/* SIZESOFTX
=========================================================================================
* Descripción: Menú de acciones del catálogo de Clientes
* Historial de cambios:
* ---------------------------------------------------------------------------------------
*    Revisión   | Fecha      | Desarrollador                    | Resumen del cambio
* ---------------------------------------------------------------------------------------
*      1       | 21/03/2026 | Lic. José Luis Aragón Cervantes  | Creación
*      2       | 21/03/2026 | Lic. José Luis Aragón Cervantes  | Estilo igual a MnuArticulosActions
*      3       | 31/05/2026 | Lic. José Luis Aragón Cervantes  | Protección anti doble click en acciones críticas
*      4       | 27/08/2026 | Lic. José Luis Aragón Cervantes  | Acción de activación móvil por QR
* ---------------------------------------------------------------------------------------
*/

import TextField from "../../ui/TextField";
import { Fragment, useEffect } from 'react';
import { useDoubleClickPrevention } from '../../../hooks/useDoubleClickPrevention';
import { Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography } from "@mui/material";
import HomeIcon from '@mui/icons-material/Home';
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RefreshIcon from "@mui/icons-material/Refresh";
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import QrCode2Icon from '@mui/icons-material/QrCode2';

import SizeSoftXModal from "../../leyout/SizeSoftXModal.jsx";

import { useClientes } from "../../../context/Catalogos/ClientesContext";
const normalizeClienteCode = (value) => String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .trim()
    .toUpperCase();

export default function MnuClientesActions() {
    const {
        handleClickRegresarMainDashboard,
        handleClickNuevoCliente,
        handleClickEditarCliente,
        handleClickGuardarCliente,
        handleClickEliminarCliente,
        handleClickDeshacer,
        handleClickRefrescarDatos,
        handleClickAsignarTarjeta,
        statusEditCliente, setStatusEditCliente,
        clienteSeleccionado,
        statusOpenModalEliminarCliente,
        setStatusOpenModalEliminarCliente,
        statusOpenModalTarjeta,
        setStatusOpenModalTarjeta,
        codigoBarras,
        setCodigoBarras,
        handleConfirmarAsignacionTarjeta,
        handleOpenModalBuscarCliente,
        statusOpenModalPrefijoClaveCliente,
        prefijoClaveCliente,
        prefijoClaveClienteError,
        handleOpenModalPrefijoClaveCliente,
        handleCloseModalPrefijoClaveCliente,
        handleChangePrefijoClaveCliente,
        handleConfirmarPrefijoClaveCliente,
        statusOpenModalActivacionCliente,
        setStatusOpenModalActivacionCliente,
        clienteActivacionQr,
        clienteActivacionQrText,
        clienteActivacionQrDataUrl,
        handleGenerarQrActivacionCliente,
    } = useClientes();

    const { isProcessing, executeWithProtection } = useDoubleClickPrevention();

    return (
        <Fragment>
            <Tooltip title={"Home"}>
                <IconButton onClick={handleClickRegresarMainDashboard}>
                    <HomeIcon className="icon" /><p className="icon">Salir</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={'Buscar Cliente (Alt+F1)'}>
                <IconButton onClick={handleOpenModalBuscarCliente}>
                    <SearchIcon className="icon" /><p className="icon">Buscar</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={'Nuevo'}>
                <IconButton disabled={isProcessing || !statusEditCliente} onClick={() => executeWithProtection(handleClickNuevoCliente)}>
                    <AddIcon className="icon" /><p className="icon">Nuevo</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={statusEditCliente ? 'Editar' : 'Guardar'}>
                <IconButton
                    disabled={isProcessing}
                    onClick={() => executeWithProtection(statusEditCliente ? handleClickEditarCliente : handleClickGuardarCliente)}
                >
                    {statusEditCliente ? <EditIcon className="icon" /> : <SaveIcon className="icon" />}
                    <p className="icon">{statusEditCliente ? 'Editar' : 'Guardar'}</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={'Eliminar'}>
                <IconButton disabled={isProcessing || statusEditCliente} onClick={() => executeWithProtection(() => setStatusOpenModalEliminarCliente(true))}>
                    <DeleteIcon className="icon" /><p className="icon">Eliminar</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={'Deshacer'}>
                <IconButton disabled={isProcessing || statusEditCliente} onClick={() => executeWithProtection(handleClickDeshacer)}>
                    <UndoIcon className="icon" /><p className="icon">Deshacer</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={'Refrescar'}>
                <IconButton onClick={handleClickRefrescarDatos}>
                    <RefreshIcon className="icon" />
                </IconButton>
            </Tooltip>
            <Tooltip title={'Prefijo claves cliente'}>
                <IconButton disabled={isProcessing || !statusEditCliente} onClick={() => executeWithProtection(handleOpenModalPrefijoClaveCliente)}>
                    <VpnKeyIcon className="icon" /><p className="icon">Prefijo</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={'Activación app clientes'}>
                <IconButton disabled={isProcessing || !statusEditCliente || !clienteSeleccionado?.clavecliente} onClick={() => executeWithProtection(handleGenerarQrActivacionCliente)}>
                    <QrCode2Icon className="icon" /><p className="icon">Activar app</p>
                </IconButton>
            </Tooltip>
            <Tooltip title={'Asignar Tarjeta'}>
                <IconButton disabled={isProcessing || statusEditCliente} onClick={() => executeWithProtection(handleClickAsignarTarjeta)}>
                    <CreditCardIcon className="icon" /><p className="icon">Tarjeta</p>
                </IconButton>
            </Tooltip>

            <SizeSoftXModal
                open={statusOpenModalEliminarCliente}
                title={"¿Está seguro de querer eliminar el cliente? Esta acción no se puede deshacer."}
                onClose={() => setStatusOpenModalEliminarCliente(false)}
                onSuccess={() => executeWithProtection(handleClickEliminarCliente)}
                success="Eliminar"
            />

            <Dialog 
                open={statusOpenModalTarjeta} 
                onClose={() => setStatusOpenModalTarjeta(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Asignación de Tarjeta</Typography>
                        <IconButton size="small" onClick={() => setStatusOpenModalTarjeta(false)} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Cliente:</strong> {clienteSeleccionado?.razonsocial}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Clave Actual:</strong> {clienteSeleccionado?.clavecliente}
                        </Typography>
                    </Box>
                    <TextField autoComplete="off"
                        autoFocus
                        fullWidth
                        label="Código de Barras"
                        value={codigoBarras}
                        onChange={(e) => setCodigoBarras(normalizeClienteCode(e.target.value))}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && codigoBarras.trim()) {
                                handleConfirmarAsignacionTarjeta();
                            }
                        }}
                        placeholder="Escanee o ingrese el código de barras"
                        size="small"
                    />
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                        Presione Enter para confirmar o haga clic en Asignar
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setStatusOpenModalTarjeta(false)} color="inherit">
                        Cancelar
                    </Button>
                    <Button 
                        onClick={() => executeWithProtection(handleConfirmarAsignacionTarjeta)} 
                        variant="contained" 
                        color="primary"
                        disabled={!codigoBarras.trim()}
                        startIcon={<CreditCardIcon />}
                    >
                        Asignar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={statusOpenModalActivacionCliente}
                onClose={() => setStatusOpenModalActivacionCliente(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">QR de activación</Typography>
                        <IconButton size="small" onClick={() => setStatusOpenModalActivacionCliente(false)} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            <strong>Cliente:</strong> {clienteActivacionQr?.razonsocial || clienteActivacionQr?.razonSocial}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                            <strong>Contacto:</strong> {clienteActivacionQr?.contacto || clienteActivacionQr?.Contacto || clienteActivacionQr?.telefono1 || ''}
                        </Typography>
                        {clienteActivacionQrDataUrl && (
                            <Box
                                component="img"
                                src={clienteActivacionQrDataUrl}
                                alt="QR de activación"
                                sx={{ width: 280, maxWidth: '100%', border: '1px solid #ddd', p: 1, borderRadius: 1 }}
                            />
                        )}
                        <TextField autoComplete="off"
                            fullWidth
                            multiline
                            minRows={2}
                            value={clienteActivacionQrText}
                            size="small"
                            sx={{ mt: 2 }}
                            InputProps={{ readOnly: true }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setStatusOpenModalActivacionCliente(false)} color="inherit">
                        Cerrar
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={statusOpenModalPrefijoClaveCliente}
                onClose={handleCloseModalPrefijoClaveCliente}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle sx={{ backgroundColor: 'primary.main', color: 'white' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Configurar prefijo de claves</Typography>
                        <IconButton size="small" onClick={handleCloseModalPrefijoClaveCliente} sx={{ color: 'white' }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        El prefijo se concatena con el folio consecutivo al crear clientes nuevos.
                    </Typography>
                    <TextField autoComplete="off"
                        autoFocus
                        fullWidth
                        label="Prefijo"
                        value={prefijoClaveCliente}
                        error={prefijoClaveClienteError}
                        helperText={prefijoClaveClienteError ? "Capture al menos una letra" : "Ejemplo: C00000001 o CL00000001"}
                        onChange={(e) => handleChangePrefijoClaveCliente(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && prefijoClaveCliente.trim()) {
                                executeWithProtection(handleConfirmarPrefijoClaveCliente);
                            }
                        }}
                        size="small"
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseModalPrefijoClaveCliente} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        onClick={() => executeWithProtection(handleConfirmarPrefijoClaveCliente)}
                        variant="contained"
                        color="primary"
                        disabled={!prefijoClaveCliente.trim() || prefijoClaveClienteError}
                        startIcon={<VpnKeyIcon />}
                    >
                        Guardar
                    </Button>
                </DialogActions>
            </Dialog>
        </Fragment>
    );
}





